/**
 * WCAG contrast, and the one operation that repairs a colour.
 *
 * ── Why this is a module and not four helpers in one page ───────────
 *
 * A full sweep of both surfaces — every dashboard route, every field
 * app panel, measured on computed styles in a real browser — found 73
 * distinct text/background pairs below the 4.5:1 WCAG AA asks for.
 * They were not one mistake repeated; they were a dozen palettes each
 * chosen for how it looked against white in a mock, and each wrong by
 * a different amount.
 *
 * The repair is always the same operation: keep the hue, walk toward
 * black (or white, on a dark ground) until it clears. Written once
 * here so a colour that has to be legible can be DERIVED rather than
 * picked and then checked by somebody who remembers to.
 *
 * ── The thresholds ──────────────────────────────────────────────────
 *
 * AA is 4.5:1 for body text and 3:1 for large text — 24px, or 18.66px
 * at weight 700. Non-text that carries meaning (an icon, a chart bar,
 * a focus ring) is 3:1 under WCAG 1.4.11. A colour used only as
 * decoration has no requirement at all, which is why a separator dot
 * is marked aria-hidden rather than darkened until it stops being a
 * separator.
 */

export const AA_TEXT = 4.5;
export const AA_LARGE = 3;
/** WCAG 1.4.11: icons, chart marks, focus rings — anything non-text that means something. */
export const AA_NON_TEXT = 3;

/** Softer than #000 and still 18:1 on white. */
export const INK_BLACK = '#111827';

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));

export const parseHex = (hex) => {
    if (typeof hex !== 'string') return null;
    let h = hex.trim();
    if (/^#[0-9a-f]{3}$/i.test(h)) {
        h = '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
    }
    if (!/^#[0-9a-f]{6}$/i.test(h)) return null;

    return [
        parseInt(h.slice(1, 3), 16),
        parseInt(h.slice(3, 5), 16),
        parseInt(h.slice(5, 7), 16),
    ];
};

export const toHex = (rgb) => '#' + rgb.map((n) => clamp(n).toString(16).padStart(2, '0')).join('');

/** Relative luminance, WCAG 2.x definition. */
export const luminance = (rgb) => {
    const [r, g, b] = rgb.map((v) => {
        const c = v / 255;

        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Contrast ratio between two hexes, 1 to 21. Unparseable input answers 1. */
export const contrast = (a, b) => {
    const ra = parseHex(a);
    const rb = parseHex(b);
    if (!ra || !rb) return 1;
    const la = luminance(ra);
    const lb = luminance(rb);

    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Walk `hex` toward `target` in twentieths and return the first step
 * that clears `need` against `against`.
 *
 * Stepping rather than solving. The ratio does not move linearly with
 * a channel scale, so a solved answer has to be verified anyway, and
 * the loop ends on `target` — which always passes, because the caller
 * only ever passes black or white.
 */
export const walkToward = (hex, target, against, need = AA_TEXT) => {
    const from = parseHex(hex);
    const to = parseHex(target);
    if (!from || !to) return target;

    for (let i = 0; i <= 20; i++) {
        const t = i / 20;
        const step = toHex([
            from[0] + (to[0] - from[0]) * t,
            from[1] + (to[1] - from[1]) * t,
            from[2] + (to[2] - from[2]) * t,
        ]);
        if (contrast(step, against) >= need) return step;
    }

    return target;
};

/**
 * `hex`, made legible on `background`, keeping its hue.
 *
 * Walks toward whichever of black or white is AWAY from the
 * background, so the same call works on a white card and on the
 * field app's dark sidebar. A colour that already passes comes back
 * untouched — the common case, and the one where changing it would
 * be a design change nobody asked for.
 */
export const readableOn = (hex, background = '#ffffff', need = AA_TEXT) => {
    if (!parseHex(hex)) return INK_BLACK;
    if (contrast(hex, background) >= need) return hex;
    const bgLight = luminance(parseHex(background) || [255, 255, 255]) > 0.35;

    return walkToward(hex, bgLight ? INK_BLACK : '#ffffff', background, need);
};

/**
 * The ink to write ON `background`: white or a dark, whichever wins.
 *
 * Best-of-those-two is never worse than 4.58:1 for ANY colour — the
 * two curves cross above the threshold — which is why this can be a
 * decision rather than a constant somebody keeps having to check.
 * The dark option falls back to pure black where the softer #111827
 * does not clear AA, which a saturated red does not.
 */
export const inkOn = (background, need = AA_TEXT) => {
    if (!parseHex(background)) return INK_BLACK;
    const dark = contrast(INK_BLACK, background) >= need ? INK_BLACK : '#000000';

    return contrast('#ffffff', background) >= contrast(dark, background) ? '#ffffff' : dark;
};
